import { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  HStack,
  Icon,
  IconButton,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Paperclip, SendHorizontal } from 'lucide-react';
import { sendFacebookMessage } from '../../../hooks/facebook';
import {
  getUtilityTemplates,
  sendUtilityTemplate,
} from '../../../hooks/facebook_utility_messages';
import { FacebookUtilityMessage } from '../../../utils/types';

interface ComposerProps {
  requires_utility_message: boolean;
  conversationId: string;
  orgId: string;
  profileId: string;
  onSent?: (m: any) => void;
}

export default function Composer({
  requires_utility_message,
  conversationId,
  orgId,
  profileId,
  onSent,
}: ComposerProps) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [templates, setTemplates] = useState<FacebookUtilityMessage[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!requires_utility_message) {
      setTemplates([]);
      return;
    }

    (async () => {
      const res = await getUtilityTemplates(orgId, profileId);
      if (res.success) {
        setTemplates(
          (Array.isArray(res.data) ? res.data : []).filter(
            (template: FacebookUtilityMessage) => template.status === 'approved',
          ),
        );
      }
    })();
  }, [requires_utility_message, orgId, profileId]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId),
    [templates, selectedTemplateId],
  );

  const send = async () => {
    try {
      setIsSending(true);

      if (requires_utility_message) {
        if (!selectedTemplateId) {
          toast({
            title: 'Select a utility message',
            description: 'This conversation currently requires an approved utility message.',
            status: 'warning',
          });
          return;
        }

        const response = await sendUtilityTemplate(orgId, conversationId, {
          template_id: selectedTemplateId,
        });

        if (!response.success) {
          throw response.data;
        }

        onSent?.(response.data);
        setSelectedTemplateId('');
        toast({
          title: 'Utility message sent',
          status: 'success',
        });
        return;
      }

      if (!text.trim() && !attachment) {
        toast({
          title: 'Message cannot be empty',
          status: 'warning',
        });
        return;
      }

      const response = await sendFacebookMessage(
        conversationId,
        attachment ? '' : text.trim(),
        orgId,
        attachment,
      );

      if (!response.success) {
        throw response.data;
      }

      onSent?.(response.data);
      setText('');
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast({
        title: 'Send failed',
        description: 'Unable to send this message right now.',
        status: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <VStack align="stretch" spacing={3}>
      {requires_utility_message ? (
        <>
          <Text fontSize="sm" color="gray.500">
            Free text is unavailable for this conversation right now. Use an approved utility message instead.
          </Text>
          <Select
            placeholder={
              templates.length
                ? 'Select approved utility message'
                : 'No approved utility messages available'
            }
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            bg="white"
            borderRadius="16px"
            h="48px"
          >
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.body_text}
              </option>
            ))}
          </Select>
          {selectedTemplate ? (
            <Text fontSize="sm" color="gray.600" bg="white" borderRadius="16px" px={4} py={3}>
              {selectedTemplate.body_text}
            </Text>
          ) : null}
        </>
      ) : (
        <HStack align="flex-end" spacing={3}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => setAttachment(e.target.files?.[0] || null)}
          />
          <IconButton
            aria-label="Attach a file"
            icon={<Icon as={Paperclip as ElementType} boxSize={4} />}
            variant="ghost"
            borderRadius="14px"
            onClick={() => fileInputRef.current?.click()}
          />
          <VStack align="stretch" spacing={2} flex="1">
            {attachment ? (
              <Text fontSize="xs" color="gray.500" px={2}>
                Attached: {attachment.name}
              </Text>
            ) : null}
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={attachment ? 'File ready to send. Send text separately if needed.' : 'Type a message...'}
              resize="none"
              minH="48px"
              maxH="160px"
              bg="white"
              borderRadius="20px"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
          </VStack>
        </HStack>
      )}

      <HStack justify="flex-end">
        <Button
          onClick={send}
          isLoading={isSending}
          bg="#1877F2"
          _hover={{ bg: '#1667d9' }}
          color="white"
          borderRadius="16px"
          leftIcon={<Icon as={SendHorizontal as ElementType} boxSize={4} />}
          isDisabled={
            requires_utility_message
              ? !selectedTemplateId || templates.length === 0
              : !text.trim() && !attachment
          }
        >
          Send
        </Button>
      </HStack>
    </VStack>
  );
}
